/**
 * @format
 * @快捷选择时间日期
 */

import React, { FC, useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import styled from 'styled-components'
import dayjs, { Dayjs } from 'dayjs'
import { _$, Datepicker, Select, Option, Icon } from '@king-design/react'
import {
  date_range_config,
  IQuickRangeDateProps,
  getRangeDateValue,
  getRangeDateShortcuts,
  renderShortcuts
} from './constants'
import { formatDate } from 'src/utils'
// ref返回的方法
export interface QuickRangeDateMethod {
  reloadDate?: () => void
}
const QuickRangeDate: FC<IQuickRangeDateProps> = forwardRef(
  (
    {
      className,
      defautlValue,
      changeValue,
      quickRange,
      showQucikBtn = true,
      optionalRange,
      optionalMaxDate,
      optionalDateRange,
      realoadIcon = true,
      dateDiff,
      showShortcuts = true,
      width = '320px',
      ...datepickerProps
    },
    ref
  ) => {
    const [dateValue, setDateValue] = useState<[number, number]>() //datepicker的value
    const [range, setRange] = useState('') // 快捷选择value
    const [currentDate, setCurrentDate] = useState<Dayjs>(optionalMaxDate || dayjs())
    const [disabledDate, setDisabledDate] = useState<{ start: Date; end: Date }>({
      start: undefined,
      end: dayjs().toDate()
    })

    // 初始化
    useEffect(() => {
      // 处理初始值 如果初始值是数组，将数组处理成dayjs
      if (Array.isArray(defautlValue)) {
        const [start, end] = defautlValue
        const quickValue = getRangeDateShortcuts([dayjs(start).valueOf(), dayjs(end).valueOf()])
        setRange(quickValue)
        setDateValue(start && end ? [dayjs(start).valueOf(), dayjs(end).valueOf()] : undefined)
      } else if (typeof defautlValue === 'string') {
        // 如果初始值是string 则设置成button的值
        const dateKey = date_range_config[defautlValue] || defautlValue || ''
        setDateValue(getRangeDateValue(dateKey))
        setRange(dateKey)
      }
    }, [defautlValue])

    // button切换日期时【触发回调】
    const btnChange = value => {
      if (value) {
        value !== range && setRange(value)
        // 如果禁用秒的话 默认秒设置为0
        const rangeDateValue = getRangeDateValue(value)
        const updateDateTime = datepickerProps.disabledSeconds
          ? rangeDateValue.map(date => dayjs(date).set('second', 0).valueOf())
          : rangeDateValue
        setDateValue(updateDateTime as [number, number])
        changeValue && changeValue(updateDateTime as [number, number], value)
      }
    }

    // 手动选择日期 && datapicker左侧快捷选择时 && 清除时 【触发回调】
    const handleChangeValue = value => {
      if (value) {
        const dateValue = value?.map(date => dayjs(date).valueOf())
        setDateValue(dateValue)
        const quickValue = getRangeDateShortcuts(dateValue)
        setRange(quickValue)
        changeValue && changeValue(dateValue || [], quickValue)
      } else {
        setCurrentDate(optionalMaxDate || dayjs())
        setDateValue(undefined)
        setRange(undefined)
        changeValue && changeValue([], undefined)
      }
    }

    // 计算disable最大/最小可选日期
    const _calcDisabledDate = (start: Date | string | number) => {
      let min = optionalDateRange
        ? dayjs(currentDate).subtract(optionalDateRange, 'day').toDate()
        : undefined
      if (dateDiff && start) {
        const calcMin = dayjs(start)?.subtract(dateDiff, 'day').toDate()
        if (dayjs(calcMin)?.diff(dayjs(min)) > 0) {
          min = calcMin
        }
        let max = dayjs(start)?.add(dateDiff, 'day').toDate()
        if (dayjs(max)?.diff(currentDate) > 0) {
          max = currentDate.toDate()
        }
        return { min, max }
      } else {
        return {
          min,
          max: currentDate.toDate()
        }
      }
    }

    // 选择开始日期后计算禁用日期
    const selectStart = value => {
      // 只选择开始时间时候，根据dateDiff计算可选择的日期
      if (dateDiff && value?.length === 1) {
        setCurrentDate(optionalMaxDate || dayjs())
        const startTime = formatDate(dayjs(value[0].$d))
        const { min, max } = _calcDisabledDate(startTime)
        setDisabledDate(({ start, end }) => ({ start: min || start, end: max || end }))
      }
      if (dateDiff && value?.length === 2) {
        setDisabledDate({
          start: optionalDateRange
            ? dayjs(currentDate).subtract(optionalDateRange, 'day').toDate()
            : undefined,
          end: currentDate.toDate()
        })
      }
    }

    // 根据当前时间重新计算日期可选范围
    // useEffect(() => {
    //   setDisabledDate({
    //     start: optionalDateRange
    //       ? dayjs(currentDate).subtract(optionalDateRange, 'day').toDate()
    //       : undefined,
    //     end: currentDate.toDate()
    //   })
    // }, [currentDate])

    // 导出重置方法
    useImperativeHandle(ref, () => ({
      reloadDate: () => reloadDate()
    }))

    // 刷新日期【触发回调】
    const reloadDate = () => {
      if (range) {
        btnChange(range)
      }
    }

    return (
      <div className={className}>
        {showQucikBtn && (
          <Select
            width={110}
            placeholder={_$('时间范围')}
            value={range}
            onChangeValue={val => {
              btnChange(val)
            }}
          >
            {quickRange.map(item => (
              <Option key={date_range_config[item]} value={date_range_config[item]}>
                {_$(item)}
              </Option>
            ))}
          </Select>
        )}
        <Datepicker
          clearable={true}
          {...datepickerProps}
          range
          type="datetime"
          value={dateValue}
          onChangeValue={handleChangeValue}
          onSelecting={selectStart}
          min={disabledDate.start}
          max={disabledDate.end}
          shortcuts={showShortcuts ? renderShortcuts : undefined}
          style={{ width }}
        />
        {realoadIcon && !!range && (
          <Icon
            className="ion-refresh cursor-pointer"
            onClick={() => {
              reloadDate()
            }}
          />
        )}
      </div>
    )
  }
)

export default styled(QuickRangeDate)`
  display: flex;
  align-items: center;
  .ion-refresh {
    margin-left: 16px;
  }
  .k-select {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  .k-datepicker {
    margin-left: -1px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
`
